import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import cn from 'classnames'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
} from '@ant-design/icons'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import Button from '@UI/New/Button'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import { useAddAlert } from '@/hooks/useAddAlert'
import { useTaskTrackerBoardAccessibleUsers } from '../../../../lib/hooks/useTaskTrackerBoardAccessibleUsers'
import {
  TaskTrackerAccessibleUser,
} from '../../../../lib/taskTrackerBoardAccessibleUsers.utils'
import { taskTrackerApi } from '../../../../api/taskTracker.api'
import {
  areTaskPerformerIdsEqual,
  formatTaskPerformerName,
  normalizeTaskPerformerIds,
} from '../../../../lib/taskTrackerPerformers.utils'
import { buildTaskStatusUpdatePayload } from '../../../../lib/taskTrackerTaskInfo.utils'
import {
  TaskTrackerBoardCard,
  TaskTrackerOnTaskUpdated,
} from '../../../../store/taskTracker.types'
import DeleteTaskModal from '../../DeleteTaskModal/DeleteTaskModal'
import TaskStickerFieldsSection from './TaskStickerFieldsSection/TaskStickerFieldsSection'
import { useTaskStickerFieldsDraft } from './TaskStickerFieldsSection/useTaskStickerFieldsDraft'
import TaskHistorySection from './TaskHistorySection/TaskHistorySection'
import TaskTrackerPermissionTooltip from '../../../TaskTrackerPermissionTooltip/TaskTrackerPermissionTooltip'
import classes from './index.module.css'

type TaskInfoTabProps = {
  task: TaskTrackerBoardCard | null
  projectId: number | null
  canEditTask?: boolean
  taskEditDisabledTooltip?: string | null
  canDeleteTask?: boolean
  taskDeleteDisabledTooltip?: string | null
  onTaskUpdated?: TaskTrackerOnTaskUpdated
  onTaskDeleted?: (taskId: number) => void
}

const iconProps = {
  onPointerEnterCapture: undefined,
  onPointerLeaveCapture: undefined,
} as const

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message
  return fallback
}

const TASK_STATUS_OPTIONS = [
  { value: 'in_progress', label: 'В работе', title: 'В работе' },
  { value: 'completed', label: 'Выполнена', title: 'Выполнена' },
]

const formatMemberName = (name: string) => name.trim() || 'Без имени'

const buildPerformerOptions = (
  users: TaskTrackerAccessibleUser[],
  task: TaskTrackerBoardCard | null,
) => {
  const optionsMap = new Map<
    string,
    { value: string; label: string; title: string }
  >()

  users.forEach((user) => {
    if (!Number.isFinite(user.userId) || user.userId <= 0) return

    const name = formatMemberName(user.name)

    optionsMap.set(String(user.userId), {
      value: String(user.userId),
      label: name,
      title: name,
    })
  })

  task?.performers.forEach((performer) => {
    if (!Number.isFinite(performer.userId) || performer.userId <= 0) return
    if (optionsMap.has(String(performer.userId))) return

    const name = formatMemberName(formatTaskPerformerName(performer))

    optionsMap.set(String(performer.userId), {
      value: String(performer.userId),
      label: name,
      title: name,
    })
  })

  return Array.from(optionsMap.values()).sort((a, b) =>
    a.title.localeCompare(b.title, 'ru'),
  )
}

const TaskInfoTab: FC<TaskInfoTabProps> = ({
  task,
  projectId,
  canEditTask = true,
  taskEditDisabledTooltip = null,
  canDeleteTask = true,
  taskDeleteDisabledTooltip = null,
  onTaskUpdated,
  onTaskDeleted,
}) => {
  const addAlert = useAddAlert()
  const {
    users: accessibleUsers,
    loading: accessibleUsersLoading,
    error: accessibleUsersError,
  } = useTaskTrackerBoardAccessibleUsers(
    task?.boardId ?? null,
    projectId,
    task != null && task.boardId != null,
  )
  const [savingPerformer, setSavingPerformer] = useState(false)
  const [savingStatus, setSavingStatus] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const handleStickerTaskUpdated = useCallback<TaskTrackerOnTaskUpdated>(
    (updatedTask) => {
      onTaskUpdated?.(updatedTask, { skipDrawerSync: true })
    },
    [onTaskUpdated],
  )
  const stickerFields = useTaskStickerFieldsDraft(task, handleStickerTaskUpdated)

  const creatorName = task?.creator?.name?.trim() || '—'

  const statusValue = task?.isCompleted ? 'completed' : 'in_progress'

  const performerOptions = useMemo(
    () => buildPerformerOptions(accessibleUsers, task),
    [accessibleUsers, task],
  )

  const taskHistoryRefreshToken = useMemo(() => {
    if (!task) return ''

    return JSON.stringify({
      id: task.id,
      columnId: task.columnId,
      isCompleted: task.isCompleted,
      performerIds: task.performerIds,
      title: task.title,
    })
  }, [task])

  const handlePerformerChange = useCallback(
    async (value: string | string[] | null) => {
      if (!task?.id || savingPerformer || !canEditTask) return

      const nextPerformerIds = normalizeTaskPerformerIds(
        (Array.isArray(value) ? value : value ? [value] : []).map(Number),
      )

      if (areTaskPerformerIdsEqual(nextPerformerIds, task.performerIds)) {
        return
      }

      setSavingPerformer(true)

      try {
        const updatedTask = await taskTrackerApi.updateTask(task.id, {
          performer_ids: nextPerformerIds,
        })

        onTaskUpdated?.(updatedTask)
      } catch (error: unknown) {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text: getErrorMessage(error, 'Не удалось изменить исполнителей'),
        })
      } finally {
        setSavingPerformer(false)
      }
    },
    [addAlert, canEditTask, onTaskUpdated, savingPerformer, task],
  )

  const handleStatusChange = useCallback(
    async (value: string | string[] | null) => {
      if (
        !task?.id ||
        savingStatus ||
        Array.isArray(value) ||
        value == null ||
        !canEditTask
      ) {
        return
      }

      const nextIsCompleted = value === 'completed'

      if (nextIsCompleted === task.isCompleted) return

      setSavingStatus(true)

      try {
        const updatedTask = await taskTrackerApi.updateTask(
          task.id,
          buildTaskStatusUpdatePayload(task, nextIsCompleted),
        )

        onTaskUpdated?.(updatedTask)
      } catch (error: unknown) {
        addAlert({
          type: 'error',
          title: 'Ошибка',
          text: getErrorMessage(error, 'Не удалось изменить статус задачи'),
        })
      } finally {
        setSavingStatus(false)
      }
    },
    [addAlert, canEditTask, onTaskUpdated, savingStatus, task],
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!task?.id || deleting || !canDeleteTask) return

    setDeleting(true)

    try {
      const result = await taskTrackerApi.deleteTask(task.id)
      if (!result.deleted) {
        throw new Error('Не удалось удалить задачу')
      }

      setDeleteModalOpen(false)
      onTaskDeleted?.(task.id)
      addAlert({
        title: 'Задача удалена',
      })
    } catch (error: unknown) {
      addAlert({
        type: 'error',
        title: 'Ошибка',
        text: getErrorMessage(error, 'Не удалось удалить задачу'),
      })
    } finally {
      setDeleting(false)
    }
  }, [addAlert, canDeleteTask, deleting, onTaskDeleted, task?.id])

  if (!task) {
    return <div className={classes.root} />
  }

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <section
          className={cn(classes.statusCard, {
            [classes.statusCardCompleted]: task.isCompleted,
          })}
        >
          <div className={classes.statusCardMain}>
            {task.isCompleted ? (
              <CheckCircleOutlined className={classes.statusIcon} {...iconProps} />
            ) : (
              <ClockCircleOutlined className={classes.statusIcon} {...iconProps} />
            )}
            <div className={classes.statusText}>
              <span className={classes.statusLabel}>Статус</span>
              <TaskTrackerPermissionTooltip
                title={taskEditDisabledTooltip}
                block
                className={classes.statusSelectTooltipWrap}
              >
                <SelectField
                  className={cn(iclasses.newSelect, classes.statusSelect)}
                  wrapperClassName={classes.statusSelectWrapper}
                  popupClassName={cn(iclasses.newSelectPopup, classes.performerSelectPopup)}
                  value={statusValue}
                  options={TASK_STATUS_OPTIONS}
                  optionLabelProp="title"
                  showSearch={false}
                  size="small"
                  loading={savingStatus}
                  disabled={savingStatus || !canEditTask}
                  getPopupContainer={() => document.body}
                  onChange={(value) => void handleStatusChange(value)}
                />
              </TaskTrackerPermissionTooltip>
            </div>
          </div>
        </section>

        <section className={classes.detailsCard}>
          <div className={classes.detailRow}>
            <div className={classes.detailIconWrap}>
              <UserOutlined className={classes.detailIcon} {...iconProps} />
            </div>
            <div className={classes.detailBody}>
              <span className={classes.detailLabel}>Автор</span>
              <span className={classes.detailValue}>{creatorName}</span>
            </div>
          </div>
        </section>

        <TaskStickerFieldsSection
          fieldRows={stickerFields.fieldRows}
          loading={stickerFields.loading}
          error={stickerFields.error}
          performerOptions={performerOptions}
          performerOptionsLoading={accessibleUsersLoading}
          performerOptionsError={accessibleUsersError}
          savingPerformers={savingPerformer}
          isFieldSaving={stickerFields.isFieldSaving}
          readOnly={!canEditTask}
          readOnlyReason={taskEditDisabledTooltip}
          onSaveValue={stickerFields.saveStickerValue}
          onSavePerformers={canEditTask ? handlePerformerChange : undefined}
        />

        <TaskHistorySection
          taskId={task.id}
          boardId={task.boardId}
          accessibleUsers={accessibleUsers}
          refreshToken={taskHistoryRefreshToken}
        />

        <section className={classes.deleteSection}>
          <p className={classes.deleteTitle}>Удаление задачи</p>
          <p className={classes.deleteText}>
            Задача будет удалена без возможности восстановления.
          </p>
          <TaskTrackerPermissionTooltip title={taskDeleteDisabledTooltip}>
            <Button
              type="button"
              color="gray"
              onClick={() => setDeleteModalOpen(true)}
              disabled={deleting || !canDeleteTask}
            >
              Удалить задачу
            </Button>
          </TaskTrackerPermissionTooltip>
        </section>
      </div>

      <DeleteTaskModal
        taskTitle={task.title}
        open={deleteModalOpen}
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onDismiss={() => {
          if (deleting) return
          setDeleteModalOpen(false)
        }}
      />
    </div>
  )
}

export default TaskInfoTab
